import PropTypes from 'prop-types';
import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from 'styled-components';
import ButtonV2 from 'components/ButtonV2';
import {
  PageShell,
  StatusPill,
  orange,
  tokens,
  useIsMobile,
} from 'components/v3';
import useFormsToaster from '../Forms/shared/useFormsToaster';
import Glyph from '../Forms/Submissions/Glyph';
import { splitUrl } from '../Forms/Submissions/PublicLinkBar';
import { sx } from '../Forms/Submissions/sx';
import QrSheet, { QrCanvas, saveQr } from './components/QrSheet';
import {
  Card,
  PageHead,
  SectionBody,
  SectionHeader,
} from './components/SectionShell';
import { useGetW9Years } from './gql';
import { copyText } from './shared/clipboard';
import { payeeFieldsOf } from './shared/payeeFields';
import { w9LinkPath, w9SettingsPath } from './shared/routes';
import { keepThrough } from './shared/taxYear';
import useW9Accounts from './shared/useW9Accounts';
import useW9Section from './shared/useW9Section';

// Settings → W-9s → Collection Link (w9-plan §6.4; design w9_app.jsx
// LinkPage): the account's one permanent link with Copy / Show QR, the
// QR code with Download PNG, what drivers fill out (the six steps, the
// payee labels inlined into step one) and where W-9s file (one row per
// tax year).

export const LINK_BLURB =
  'Drivers open it on any device and fill out the six-step W-9. Each W-9 files under the tax year it’s signed in, so the link stays the same every year.';
export const QR_CARD_BLURB =
  'Print it for the sign-in window, pit gate, or payout booth.';
export const FILE_NOTES = [
  'Started in one year and signed in the next? It files under the year it’s signed.',
  'A new tax year opens on its own on Jan 1. Nothing to set up, and your link stays the same.',
];

// "Driver Name, Mobile Number, Division and Car Number"
export const joinLabels = (labels) => {
  const list = labels.filter(Boolean);
  if (list.length <= 1) return list.join('');
  return `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
};

// The six wizard steps (w9-plan §2.2) as the driver meets them.
export const stepsFor = (payeeLabels) => [
  {
    title: 'Driver info',
    body: payeeLabels.length
      ? `Your questions: ${joinLabels(payeeLabels)}`
      : 'Your own questions (none yet)',
  },
  {
    title: 'Taxpayer Information',
    body: 'Name as shown on the income tax return, and the business name if different (lines 1–2)',
  },
  {
    title: 'Tax Classification',
    body: 'Federal tax classification (line 3a) and foreign partners, owners or beneficiaries (3b)',
  },
  {
    title: 'Address & Exemptions',
    body: 'Street, city, state and ZIP, exemption codes and account numbers (lines 4–7)',
  },
  {
    title: 'Taxpayer Identification Number',
    body: 'SSN or EIN, checked for format, encrypted and masked',
  },
  {
    title: 'Certification & Signature',
    body: 'E-signature under penalties of perjury, typed or drawn',
  },
];

export const yearRow = (y) => ({
  year: y.year,
  title:
    y.status === 'archived'
      ? 'Archived · read-only'
      : `Signed Jan 1 – Dec 31, ${y.year}`,
  sub:
    y.status === 'archived'
      ? `${Number(y.count || 0).toLocaleString()} ${
          y.count === 1 ? 'W-9' : 'W-9s'
        }, kept through ${keepThrough(y.year)}`
      : 'New W-9s file here now',
  pill: y.status === 'archived' ? 'Archived' : 'Current',
});

const Para = ({ children, small }) => {
  const t = tokens(useTheme());
  return (
    <p
      style={{
        margin: 0,
        fontSize: small ? 13 : 14,
        lineHeight: 1.55,
        color: t.sub,
        maxWidth: 620,
      }}
    >
      {children}
    </p>
  );
};

Para.propTypes = { children: PropTypes.node.isRequired, small: PropTypes.bool };
Para.defaultProps = { small: false };

const LinkBody = ({ form, routePrefix, showToast }) => {
  const theme = useTheme();
  const t = tokens(theme);
  const s = sx(theme);
  const isMobile = useIsMobile();
  const [qrOpen, setQrOpen] = React.useState(false);
  const { years, loading: yearsLoading } = useGetW9Years(form.id);
  const url = form.w9_url || '';
  const [scheme, rest] = splitUrl(url);
  const payeeLabels = payeeFieldsOf(form).map((f) => f.label);
  const steps = stepsFor(payeeLabels);
  const rows = [...years].sort((a, b) => b.year - a.year).map(yearRow);
  const wide = !isMobile;
  const button = { width: isMobile ? '100%' : 'auto', whiteSpace: 'nowrap' };

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <PageHead
        title="Collection Link"
        sub={`One permanent link for ${form.account.name}`}
      />
      <Card
        style={{
          padding: isMobile ? 16 : '22px 24px',
          borderColor: orange(0.28),
          background: `linear-gradient(90deg, ${orange(0.1)}, ${orange(
            0.02
          )}), ${t.card}`,
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '1px',
            textTransform: 'uppercase',
            color: t.primary,
          }}
        >
          Your W-9 collection link
        </div>
        <div
          style={{
            margin: '6px 0 10px',
            fontSize: isMobile ? 17 : 22,
            letterSpacing: '-0.2px',
            color: t.sub,
            overflowWrap: 'anywhere',
          }}
          data-w9-link-url=""
        >
          {scheme}
          <strong style={{ color: t.text, fontWeight: 800 }}>{rest}</strong>
        </div>
        <Para>{LINK_BLURB}</Para>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            marginTop: 14,
          }}
        >
          <ButtonV2
            primary
            onClick={() => copyText(url, 'W-9 link', showToast)}
            icon={<Glyph name="copy" size={15} />}
            style={{ ...button, flex: isMobile ? '1 1 0' : undefined }}
          >
            Copy link
          </ButtonV2>
          <ButtonV2
            onClick={() => setQrOpen(true)}
            icon={<Glyph name="grid-3x3" size={15} />}
            style={{ ...button, flex: isMobile ? '1 1 0' : undefined }}
          >
            Show QR code
          </ButtonV2>
        </div>
      </Card>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: wide
            ? 'minmax(0, 1fr) minmax(0, 1fr)'
            : 'minmax(0, 1fr)',
          gap: 16,
          alignItems: 'start',
        }}
      >
        <Card title="QR code">
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 18,
              padding: isMobile ? 16 : '16px 18px 18px',
            }}
          >
            <QrCanvas text={url} size={132} />
            <div
              style={{
                flex: '1 1 180px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 10,
                minWidth: 0,
              }}
            >
              <Para small>{QR_CARD_BLURB}</Para>
              <ButtonV2
                onClick={() => saveQr(url, form.slug, showToast)}
                icon={<Glyph name="download" size={15} />}
                style={button}
              >
                Download PNG
              </ButtonV2>
              <div
                style={{
                  fontSize: 11.5,
                  color: t.faint,
                  overflowWrap: 'anywhere',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                Scans open {rest}
              </div>
            </div>
          </div>
        </Card>

        <Card
          title="What drivers fill out"
          action={
            <Link
              to={w9SettingsPath(routePrefix, { account: form.account.id })}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 2,
                minHeight: 36,
                padding: '0 8px',
                borderRadius: 8,
                color: s.otext,
                fontSize: 13,
                fontWeight: 700,
                textDecoration: 'none',
                whiteSpace: 'nowrap',
              }}
            >
              Edit payee details
              <Glyph name="chevron-right" size={14} />
            </Link>
          }
        >
          <ol style={{ listStyle: 'none', margin: 0, padding: '4px 16px 8px' }}>
            {steps.map((step, i) => (
              <li
                key={step.title}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 12,
                  padding: '11px 0',
                  borderTop: i ? '1px solid ' + t.line : 'none',
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    flex: 'none',
                    width: 26,
                    height: 26,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: s.tint,
                    border: '1px solid ' + s.tintB,
                    color: s.otext,
                    fontSize: 12.5,
                    fontWeight: 800,
                  }}
                >
                  {i + 1}
                </span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: t.text }}>
                    {step.title}
                  </div>
                  <div
                    style={{
                      marginTop: 1,
                      fontSize: 12.5,
                      lineHeight: 1.45,
                      color: t.sub,
                    }}
                  >
                    {step.body}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </Card>
      </div>

      <Card title="Where W-9s file">
        <div aria-busy={yearsLoading && !rows.length ? 'true' : undefined}>
          {rows.map((row, i) => (
            <div
              key={row.year}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                minHeight: 56,
                padding: '8px 16px',
                borderTop: i ? '1px solid ' + t.line : 'none',
              }}
            >
              <span
                style={{
                  flex: 'none',
                  minWidth: 52,
                  fontSize: 18,
                  fontWeight: 800,
                  letterSpacing: '-0.3px',
                  color: t.text,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {row.year}
              </span>
              <span
                style={{
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <span
                  style={{ fontSize: 13.5, fontWeight: 600, color: t.text }}
                >
                  {row.title}
                </span>
                <span style={{ marginTop: 1, fontSize: 12, color: t.faint }}>
                  {row.sub}
                </span>
              </span>
              {row.pill === 'Current' ? (
                <StatusPill
                  label="Current"
                  color={s.ok}
                  background={s.okBg}
                  border={s.okBg}
                />
              ) : (
                <StatusPill
                  label="Archived"
                  color={t.sub}
                  background={s.fill}
                  border={s.line2}
                />
              )}
            </div>
          ))}
          {!rows.length && !yearsLoading ? (
            <div style={{ padding: '14px 16px', fontSize: 13, color: t.faint }}>
              The current tax year appears here once the first W-9 is signed.
            </div>
          ) : null}
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            padding: '12px 16px 14px',
            borderTop: '1px solid ' + t.line,
            background: t.inset,
          }}
        >
          {FILE_NOTES.map((note, i) => (
            <div
              key={note}
              style={{
                display: 'flex',
                gap: 10,
                fontSize: 12.5,
                lineHeight: 1.5,
                color: t.sub,
              }}
            >
              <span style={{ flex: 'none', marginTop: 2, color: t.faint }}>
                <Glyph name={i ? 'rotate-ccw' : 'pen'} size={15} />
              </span>
              <span>{note}</span>
            </div>
          ))}
        </div>
      </Card>

      {url ? (
        <QrSheet
          open={qrOpen}
          url={url}
          slug={form.slug}
          accountName={form.account.name}
          onClose={() => setQrOpen(false)}
          onToast={showToast}
        />
      ) : null}
    </div>
  );
};

LinkBody.propTypes = {
  form: PropTypes.shape({
    id: PropTypes.number.isRequired,
    slug: PropTypes.string,
    w9_url: PropTypes.string,
    account: PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
    }).isRequired,
    working_version: PropTypes.object,
  }).isRequired,
  routePrefix: PropTypes.string.isRequired,
  showToast: PropTypes.func.isRequired,
};

const LinkPage = () => {
  const section = useW9Section(w9LinkPath);
  const { accounts, loading: accountsLoading } = useW9Accounts();
  const { showToast, toasterNode } = useFormsToaster();
  return (
    <PageShell>
      <SectionHeader
        page="link"
        routePrefix={section.routePrefix}
        accountId={section.accountId}
        accounts={accounts}
        accountsLoading={accountsLoading}
        onPickAccount={section.pickAccount}
      />
      <SectionBody
        accountId={section.accountId}
        form={section.form}
        loading={section.loading}
        error={section.error}
        onRetry={section.retry}
      >
        {(form) => (
          <LinkBody
            key={form.id}
            form={form}
            routePrefix={section.routePrefix}
            showToast={showToast}
          />
        )}
      </SectionBody>
      {toasterNode}
    </PageShell>
  );
};

export default LinkPage;
